import { NgForOf, NgIf } from "@angular/common";
import { Component, Input } from "@angular/core";
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import {
  CANONICAL_TIME_BANDS,
  findCanonicalTimeBandByKey,
  findPersistedCanonicalTimeBand,
} from "app/shared/constants/time-bands";
import {
  findOverlappingWeeklyIntervals,
  isOvernightInterval,
  parseTimeMinutes,
} from "app/shared/utils/weekly-time-interval";

export const COURT_PRICING_DAYS = [
  { value: 1, short: "Mon", label: "Monday" },
  { value: 2, short: "Tue", label: "Tuesday" },
  { value: 3, short: "Wed", label: "Wednesday" },
  { value: 4, short: "Thu", label: "Thursday" },
  { value: 5, short: "Fri", label: "Friday" },
  { value: 6, short: "Sat", label: "Saturday" },
  { value: 0, short: "Sun", label: "Sunday" },
];

export const COURT_PRICING_TIME_OPTIONS = Array.from(
  { length: 48 },
  (_, index) => {
    const hours = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? "00" : "30";
    const value = `${String(hours).padStart(2, "0")}:${minutes}`;
    return { label: value, value };
  },
);

export type PricingIntervalMode = "predefined" | "custom";

export function thirtyMinuteTimeValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === "") return null;
  const minutes = parseTimeMinutes(value);
  if (minutes === null) return { invalidTime: true };
  return minutes % 30 === 0 ? null : { thirtyMinuteInterval: true };
}

function rawRuleValue(controlOrValue: AbstractControl | any): any {
  return typeof controlOrValue?.getRawValue === "function"
    ? controlOrValue.getRawValue()
    : controlOrValue || {};
}

export function customPricingRuleTimeOrderValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = rawRuleValue(control);
  if (value.intervalMode !== "custom") return null;
  const start = parseTimeMinutes(value.startTime);
  const end = parseTimeMinutes(value.endTime);
  if (start === null || end === null || start === end) return null;
  return start < end ? null : { customTimeOrder: true };
}

export function effectivePricingRuleInterval(
  controlOrValue: AbstractControl | any,
): any {
  const value = rawRuleValue(controlOrValue);
  if (value.intervalMode !== "predefined") return value;
  const band = findCanonicalTimeBandByKey(value.timeBand);
  return {
    ...value,
    startTime: band?.startTime || "",
    endTime: band?.endTime || "",
  };
}

export function serializePricingRule(controlOrValue: AbstractControl | any) {
  const value = rawRuleValue(controlOrValue);
  const common = {
    daysOfWeek: value.daysOfWeek,
    pricePerHour: value.pricePerHour,
  };
  if (value.intervalMode === "predefined") {
    return { ...common, timeBand: value.timeBand };
  }
  return {
    ...common,
    startTime: value.startTime,
    endTime: value.endTime,
  };
}

export function pricingRulesValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const rules =
    control instanceof UntypedFormArray
      ? control.controls.map(effectivePricingRuleInterval)
      : [];
  const hasInvalidRange = rules.some((rule: any) => {
    const start = parseTimeMinutes(rule.startTime);
    const end = parseTimeMinutes(rule.endTime);
    return start !== null && end !== null && start === end;
  });
  if (hasInvalidRange) return { zeroLengthRule: true };
  return findOverlappingWeeklyIntervals(rules)
    ? { overlappingRules: true }
    : null;
}

export function buildPricingRuleFormGroup(
  formBuilder: UntypedFormBuilder,
  value: any = {},
): AbstractControl {
  const persistedBand = findPersistedCanonicalTimeBand(value);
  const hasStoredValue = Object.keys(value || {}).length > 0;
  const intervalMode: PricingIntervalMode =
    value.intervalMode === "predefined" || value.intervalMode === "custom"
      ? value.intervalMode
      : persistedBand
        ? "predefined"
        : hasStoredValue
          ? "custom"
          : "predefined";
  const rule = formBuilder.group(
    {
      intervalMode: [intervalMode, Validators.required],
      timeBand: [persistedBand?.key || value.timeBand || null],
      startTime: [
        value.startTime || "",
        [Validators.required, thirtyMinuteTimeValidator],
      ],
      endTime: [
        value.endTime || "",
        [Validators.required, thirtyMinuteTimeValidator],
      ],
      daysOfWeek: [
        value.daysOfWeek || [1, 2, 3, 4, 5],
        [Validators.required, Validators.minLength(1)],
      ],
      pricePerHour: [
        value.pricePerHour ?? null,
        [Validators.required, Validators.min(0)],
      ],
    },
    { validators: customPricingRuleTimeOrderValidator },
  );
  configurePricingRuleMode(rule, intervalMode);
  return rule;
}

export function configurePricingRuleMode(
  rule: AbstractControl,
  mode: PricingIntervalMode,
): void {
  const timeBand = rule.get("timeBand");
  const startTime = rule.get("startTime");
  const endTime = rule.get("endTime");

  if (mode === "predefined") {
    timeBand?.setValidators([
      Validators.required,
      Validators.pattern(/^(morning|offpeak|evening|overnight)$/),
    ]);
    timeBand?.enable({ emitEvent: false });
    startTime?.disable({ emitEvent: false });
    endTime?.disable({ emitEvent: false });
  } else {
    timeBand?.clearValidators();
    timeBand?.disable({ emitEvent: false });
    startTime?.enable({ emitEvent: false });
    endTime?.enable({ emitEvent: false });
  }
  timeBand?.updateValueAndValidity({ emitEvent: false });
  startTime?.updateValueAndValidity({ emitEvent: false });
  endTime?.updateValueAndValidity({ emitEvent: false });
}

@Component({
  selector: "app-court-pricing-rules",
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: "./court-pricing-rules.component.html",
  styleUrls: ["./court-pricing-rules.component.scss"],
})
export class CourtPricingRulesComponent {
  @Input({ required: true }) rules!: UntypedFormArray;

  readonly days = COURT_PRICING_DAYS;
  readonly customTimeOptions = COURT_PRICING_TIME_OPTIONS;
  readonly timeBands = CANONICAL_TIME_BANDS;
  constructor(private formBuilder: UntypedFormBuilder) {}

  addRule(): void {
    this.rules.push(this.createRule());
    this.rules.markAsDirty();
  }

  removeRule(index: number): void {
    this.rules.removeAt(index);
    this.rules.markAsDirty();
  }

  isDaySelected(rule: AbstractControl, day: number): boolean {
    return (rule.get("daysOfWeek")?.value || []).includes(day);
  }

  isOvernight(rule: AbstractControl): boolean {
    return isOvernightInterval(effectivePricingRuleInterval(rule));
  }

  isPredefined(rule: AbstractControl): boolean {
    return rule.get("intervalMode")?.value === "predefined";
  }

  isStartTimeAllowed(rule: AbstractControl, candidate: string): boolean {
    const candidateMinutes = parseTimeMinutes(candidate);
    const endMinutes = parseTimeMinutes(rule.get("endTime")?.value);
    const candidateIndex = this.customTimeOptions.findIndex(
      (option) => option.value === candidate,
    );
    const endTime =
      endMinutes === null
        ? this.customTimeOptions[candidateIndex + 1]?.value
        : rule.get("endTime")?.value;
    return (
      candidateMinutes !== null &&
      endTime !== undefined &&
      (endMinutes === null || candidateMinutes < endMinutes) &&
      !this.overlapsOtherRule(rule, {
        intervalMode: "custom",
        startTime: candidate,
        endTime,
      })
    );
  }

  isEndTimeAllowed(rule: AbstractControl, candidate: string): boolean {
    const startMinutes = parseTimeMinutes(rule.get("startTime")?.value);
    const candidateMinutes = parseTimeMinutes(candidate);
    const candidateIndex = this.customTimeOptions.findIndex(
      (option) => option.value === candidate,
    );
    const startTime =
      startMinutes === null
        ? this.customTimeOptions[candidateIndex - 1]?.value
        : rule.get("startTime")?.value;
    return (
      candidateMinutes !== null &&
      startTime !== undefined &&
      (startMinutes === null || candidateMinutes > startMinutes) &&
      !this.overlapsOtherRule(rule, {
        intervalMode: "custom",
        startTime,
        endTime: candidate,
      })
    );
  }

  isTimeBandAllowed(rule: AbstractControl, timeBand: string): boolean {
    return !this.overlapsOtherRule(rule, {
      intervalMode: "predefined",
      timeBand,
    });
  }

  isDaySelectionAllowed(rule: AbstractControl, day: number): boolean {
    const currentDays = rule.get("daysOfWeek")?.value || [];
    if (currentDays.includes(day)) return true;
    return !this.overlapsOtherRule(rule, {
      daysOfWeek: [...currentDays, day],
    });
  }

  setIntervalMode(rule: AbstractControl, mode: PricingIntervalMode): void {
    rule.get("intervalMode")?.setValue(mode, { emitEvent: false });
    configurePricingRuleMode(rule, mode);
    rule.updateValueAndValidity({ emitEvent: false });
    this.rules.updateValueAndValidity();
    this.rules.markAsDirty();
  }

  selectTimeBand(rule: AbstractControl, timeBand: string): void {
    rule.get("timeBand")?.setValue(timeBand, { emitEvent: false });
    rule.updateValueAndValidity({ emitEvent: false });
    this.rules.updateValueAndValidity();
    this.rules.markAsDirty();
  }

  toggleDay(
    rule: AbstractControl,
    day: number,
    event: MatCheckboxChange,
  ): void {
    const control = rule.get("daysOfWeek");
    const current = new Set<number>(control?.value || []);
    event.checked ? current.add(day) : current.delete(day);
    control?.setValue([...current].sort((a, b) => a - b));
    control?.markAsTouched();
    this.rules.updateValueAndValidity();
  }

  private createRule(): AbstractControl {
    return buildPricingRuleFormGroup(this.formBuilder);
  }

  private overlapsOtherRule(
    rule: AbstractControl,
    overrides: Record<string, unknown>,
  ): boolean {
    const candidate = effectivePricingRuleInterval({
      ...rawRuleValue(rule),
      ...overrides,
    });
    const start = parseTimeMinutes(candidate.startTime);
    const end = parseTimeMinutes(candidate.endTime);
    if (start === null || end === null || start === end) return false;

    return this.rules.controls.some(
      (otherRule) =>
        otherRule !== rule &&
        findOverlappingWeeklyIntervals([
          effectivePricingRuleInterval(otherRule),
          candidate,
        ]) !== null,
    );
  }
}
